import { useRef, useState } from 'react'
import type { PhotoStore } from '@application/ports.ts'
import type { Step } from '@domain/events.ts'
import type { Task } from '@domain/state.ts'
import type { PhotoId, StepId, TaskId } from '@domain/ids.ts'
import { es } from '../strings/es.ts'
import { usePhoto } from '../child/usePhoto.ts'

const MAX_STEPS = 8

/**
 * Editing the agreement.
 *
 * The step list is the important part. A child who stops at the same step every
 * day is usually looking at a step that is really three things, and splitting it
 * in two is the whole fix. That is why the hint sits right above the list rather
 * than in a manual nobody reads.
 */
export function TaskEditor({
  task,
  photos,
  onRename,
  onSteps,
  onSetPhoto,
  onArchive,
  onRetune,
}: {
  task: Task
  photos: PhotoStore | null
  onRename: (taskId: TaskId, name: string, emoji: string) => void
  onSteps: (taskId: TaskId, steps: readonly Step[]) => void
  onSetPhoto: (taskId: TaskId, photoId: PhotoId) => void
  onArchive: (taskId: TaskId, reason: string) => void
  onRetune: (taskId: TaskId, coins: number, xp: number) => void
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(task.name)
  const [emoji, setEmoji] = useState(task.emoji ?? '')
  const [steps, setSteps] = useState<Step[]>([...task.steps])
  // La paga se edita en el primer peldaño, que es el que se ve y el que se
  // acuerda. Los demás salen de la misma escalera que usa la semilla, así que
  // no hay cuatro números que cuadrar a mano.
  const [coins, setCoins] = useState(task.rewards[1].coins)
  const [xp, setXp] = useState(task.rewards[1].xp)
  const [archiving, setArchiving] = useState(false)
  const [reason, setReason] = useState('')
  const [saved, setSaved] = useState(false)
  const url = usePhoto(photos, task.referencePhotoId)
  const fileInput = useRef<HTMLInputElement>(null)

  const move = (from: number, to: number): void => {
    if (to < 0 || to >= steps.length) return
    const next = [...steps]
    const [moved] = next.splice(from, 1)
    if (moved) next.splice(to, 0, moved)
    setSteps(next)
  }

  const save = (): void => {
    if (name.trim() !== task.name || emoji !== (task.emoji ?? '')) {
      onRename(task.id, name.trim(), emoji)
    }
    const changed =
      steps.length !== task.steps.length ||
      steps.some((s, i) => s.id !== task.steps[i]?.id || s.text !== task.steps[i]?.text)
    if (changed) onSteps(task.id, steps)
    if (coins !== task.rewards[1].coins || xp !== task.rewards[1].xp) {
      onRetune(task.id, task.kind === 'routine' ? 0 : coins, xp)
    }
    setSaved(true)
    setTimeout(() => {
      setSaved(false)
    }, 2000)
  }

  const capture = async (file: File): Promise<void> => {
    if (!photos) return
    onSetPhoto(task.id, await photos.put(file, 'reference'))
  }

  return (
    <li className="border-rule bg-surface m-0 rounded-xl border p-4">
      <div className="flex items-center gap-3">
        <span className="min-w-0 flex-1">
          <span className="block font-bold">
            {task.emoji} {task.name}
          </span>
          <span className={`block text-sm ${url ? 'text-mint' : 'text-muted'}`}>
            {url ? es.config.hasPhoto : es.config.noPhoto} ·{' '}
            {task.steps.length === 1 ? '1 paso' : `${task.steps.length} pasos`}
          </span>
        </span>
        <button
          type="button"
          className="border-rule shrink-0 rounded-lg border px-4 text-sm font-bold"
          aria-expanded={open}
          onClick={() => {
            setOpen(!open)
          }}
        >
          {open ? es.config.close : es.config.edit}
        </button>
      </div>

      {open ? (
        <div className="mt-4">
          <div className="mb-4 flex gap-3">
            <span className="w-20">
              <label className="mb-1 block text-sm font-bold" htmlFor={`emoji-${task.id}`}>
                {es.config.emoji}
              </label>
              <input
                id={`emoji-${task.id}`}
                className="border-rule w-full rounded-lg border px-3 text-center text-2xl"
                maxLength={4}
                value={emoji}
                onChange={(e) => {
                  setEmoji(e.target.value)
                }}
              />
            </span>
            <span className="flex-1">
              <label className="mb-1 block text-sm font-bold" htmlFor={`name-${task.id}`}>
                {es.config.name}
              </label>
              <input
                id={`name-${task.id}`}
                className="border-rule w-full rounded-lg border px-3"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                }}
              />
            </span>
          </div>

          <h4 className="text-muted mt-5 mb-1 text-xs font-bold tracking-[0.1em] uppercase">
            {es.config.steps}
          </h4>
          <p className="text-muted mt-0 mb-3 text-sm">{es.config.splitHint}</p>

          <ul className="grid list-none gap-2 p-0">
            {steps.map((stepItem, index) => (
              <li key={stepItem.id} className="m-0 flex items-center gap-2">
                <input
                  className="border-rule min-w-0 flex-1 rounded-lg border px-3"
                  aria-label={`${es.config.steps} ${index + 1}`}
                  value={stepItem.text}
                  onChange={(e) => {
                    setSteps(
                      steps.map((s, i) => (i === index ? { ...s, text: e.target.value } : s)),
                    )
                  }}
                />
                <button
                  type="button"
                  className="border-rule min-h-11 shrink-0 rounded-lg border px-3"
                  aria-label={`${es.config.up}: ${stepItem.text}`}
                  disabled={index === 0}
                  onClick={() => {
                    move(index, index - 1)
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="border-rule min-h-11 shrink-0 rounded-lg border px-3"
                  aria-label={`${es.config.down}: ${stepItem.text}`}
                  disabled={index === steps.length - 1}
                  onClick={() => {
                    move(index, index + 1)
                  }}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="text-coral border-rule min-h-11 shrink-0 rounded-lg border px-3 font-bold"
                  aria-label={`${es.config.removeStep}: ${stepItem.text}`}
                  onClick={() => {
                    setSteps(steps.filter((_, i) => i !== index))
                  }}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>

          {steps.length < MAX_STEPS ? (
            <button
              type="button"
              className="border-rule mt-3 rounded-lg border px-4 text-sm font-bold"
              onClick={() => {
                setSteps([
                  ...steps,
                  { id: `${task.id}-n${Date.now().toString(36)}` as StepId, text: '' },
                ])
              }}
            >
              {es.config.addStep}
            </button>
          ) : (
            <p className="text-muted mt-3 mb-0 text-sm">{es.config.maxSteps}</p>
          )}

          <h4 className="text-muted mt-6 mb-2 text-xs font-bold tracking-[0.1em] uppercase">
            {es.config.pays}
          </h4>
          {task.kind === 'routine' ? (
            // No hay casilla de monedas, y eso no es un olvido: la regla es la
            // forma del formulario, igual que al crear la tarea.
            <p className="text-muted mt-0 mb-2 text-sm">{es.config.paysRoutine}</p>
          ) : (
            <div className="mb-2 flex flex-wrap items-end gap-4">
              <label className="text-sm font-bold">
                {es.config.paysCoins}
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={coins}
                  onChange={(e) => {
                    setCoins(Number(e.target.value))
                  }}
                  className="border-rule bg-surface mt-1 block w-24 rounded-lg border-2 p-2"
                />
              </label>
              <label className="text-sm font-bold">
                {es.config.paysXp}
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={xp}
                  onChange={(e) => {
                    setXp(Number(e.target.value))
                  }}
                  className="border-rule bg-surface mt-1 block w-24 rounded-lg border-2 p-2"
                />
              </label>
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="bg-teal rounded-lg px-6 font-bold text-white"
              onClick={save}
            >
              {es.config.save}
            </button>
            <button
              type="button"
              className="border-rule rounded-lg border px-5 text-sm font-bold"
              onClick={() => fileInput.current?.click()}
            >
              📷 {url ? es.config.changePhoto : es.config.setPhoto}
            </button>
            {saved ? (
              <span role="status" className="text-mint text-sm font-bold">
                {es.config.saved}
              </span>
            ) : null}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            aria-label={`${es.config.setPhoto}: ${task.name}`}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void capture(file)
              e.target.value = ''
            }}
          />

          {url ? (
            <img src={url} alt="" className="mt-4 max-h-48 w-full rounded-lg object-cover" />
          ) : null}

          <div className="border-rule mt-5 border-t pt-4">
            {archiving ? (
              <>
                <label className="mb-1 block text-sm font-bold" htmlFor={`arch-${task.id}`}>
                  {es.config.archiveReason}
                </label>
                <input
                  id={`arch-${task.id}`}
                  className="border-rule mb-3 w-full rounded-lg border px-3"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value)
                  }}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={reason.trim().length === 0}
                    className="bg-coral rounded-lg px-5 font-bold text-white disabled:opacity-40"
                    onClick={() => {
                      onArchive(task.id, reason)
                      setArchiving(false)
                      setReason('')
                    }}
                  >
                    {es.config.archive}
                  </button>
                  <button
                    type="button"
                    className="border-rule rounded-lg border px-5 font-bold"
                    onClick={() => {
                      setArchiving(false)
                    }}
                  >
                    {es.pin.cancel}
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                className="text-coral text-sm font-bold underline underline-offset-4"
                onClick={() => {
                  setArchiving(true)
                }}
              >
                {es.config.archive}
              </button>
            )}
          </div>
        </div>
      ) : null}
    </li>
  )
}
